"use client";

import { useMemo, useState } from "react";
import {
  calculateMortgageEstimate,
  MORTGAGE_LOAN_TERMS,
  type MortgageLoanTerm,
} from "./mortgage";

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

export function MortgageCalculator({
  initialHomePrice,
}: {
  initialHomePrice: number | null;
}) {
  const [homePrice, setHomePrice] = useState(
    initialHomePrice === null ? "" : String(initialHomePrice),
  );
  const [downPaymentPercent, setDownPaymentPercent] = useState("20");
  const [annualInterestRate, setAnnualInterestRate] = useState("6.5");
  const [termYears, setTermYears] = useState<MortgageLoanTerm>(30);

  const parsed = useMemo(() => {
    const price = Number(homePrice);
    const down = Number(downPaymentPercent);
    const rate = Number(annualInterestRate);
    const estimate = calculateMortgageEstimate({
      homePrice: price,
      downPaymentPercent: down,
      annualInterestRate: rate,
      termYears,
    });
    let error: string | null = null;
    if (homePrice.trim() === "" || !Number.isFinite(price) || price <= 0) {
      error = "Enter a home price greater than zero.";
    } else if (
      downPaymentPercent.trim() === "" ||
      !Number.isFinite(down) ||
      down < 0 ||
      down > 100
    ) {
      error = "Down payment must be between 0% and 100%.";
    } else if (
      annualInterestRate.trim() === "" ||
      !Number.isFinite(rate) ||
      rate < 0
    ) {
      error = "Interest rate must be zero or greater.";
    }
    return { estimate, error };
  }, [annualInterestRate, downPaymentPercent, homePrice, termYears]);

  const inputClass =
    "w-full appearance-none rounded-md border px-3 py-2 text-right text-sm outline-none focus-visible:ring-2 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";
  const inputStyle = {
    borderColor: "var(--brand-card-border)",
    backgroundColor: "var(--brand-card-bg)",
    color: "var(--brand-text)",
  };

  return (
    <section
      aria-labelledby="mortgage-calculator-heading"
      className="rounded-lg border p-5"
      style={{
        borderColor: "var(--brand-card-border)",
        backgroundColor: "var(--brand-card-bg)",
      }}
    >
      <h2
        id="mortgage-calculator-heading"
        className="text-lg font-semibold"
        style={{
          color: "var(--brand-text)",
          fontFamily: "var(--brand-font-heading)",
        }}
      >
        Mortgage calculator
      </h2>

      <div className="mt-4 flex flex-col gap-3">
        <label className="grid grid-cols-[minmax(0,1fr)_minmax(9rem,12rem)] items-center gap-3 text-sm">
          <span style={{ color: "var(--brand-text-muted)" }}>Home price</span>
          <input
            aria-label="Home price"
            type="number"
            min="0"
            step="1000"
            value={homePrice}
            onChange={(event) => setHomePrice(event.target.value)}
            className={inputClass}
            style={inputStyle}
          />
        </label>

        <label className="grid grid-cols-[minmax(0,1fr)_minmax(9rem,12rem)] items-center gap-3 text-sm">
          <span style={{ color: "var(--brand-text-muted)" }}>Down payment</span>
          <span className="relative">
            <input
              aria-label="Down payment percentage"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={downPaymentPercent}
              onChange={(event) => setDownPaymentPercent(event.target.value)}
              className={`${inputClass} pr-8`}
              style={inputStyle}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm"
              style={{ color: "var(--brand-text-muted)" }}
            >
              %
            </span>
          </span>
        </label>
        {parsed.estimate && (
          <p className="-mt-1 text-right text-xs" style={{ color: "var(--brand-text-muted)" }}>
            {USD.format(parsed.estimate.downPaymentAmount)} down
          </p>
        )}

        <label className="grid grid-cols-[minmax(0,1fr)_minmax(9rem,12rem)] items-center gap-3 text-sm">
          <span style={{ color: "var(--brand-text-muted)" }}>Interest rate</span>
          <span className="relative">
            <input
              aria-label="Interest rate"
              type="number"
              min="0"
              step="0.01"
              value={annualInterestRate}
              onChange={(event) => setAnnualInterestRate(event.target.value)}
              className={`${inputClass} pr-8`}
              style={inputStyle}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm"
              style={{ color: "var(--brand-text-muted)" }}
            >
              %
            </span>
          </span>
        </label>

        <label className="grid grid-cols-[minmax(0,1fr)_minmax(9rem,12rem)] items-center gap-3 text-sm">
          <span style={{ color: "var(--brand-text-muted)" }}>Loan term</span>
          <select
            aria-label="Loan term"
            value={termYears}
            onChange={(event) => setTermYears(Number(event.target.value) as MortgageLoanTerm)}
            className={`${inputClass} text-left`}
            style={inputStyle}
          >
            {MORTGAGE_LOAN_TERMS.map((term) => (
              <option key={term} value={term}>
                {term}-year
              </option>
            ))}
          </select>
        </label>
      </div>

      {parsed.error ? (
        <p
          role="alert"
          className="mt-4 text-sm"
          style={{ color: "var(--brand-danger, var(--brand-primary))" }}
        >
          {parsed.error}
        </p>
      ) : parsed.estimate ? (
        <div
          className="mt-4 rounded-lg p-4"
          style={{ backgroundColor: "var(--brand-surface-subtle)" }}
        >
          <p className="text-xs" style={{ color: "var(--brand-text-muted)" }}>
            Loan amount {USD.format(parsed.estimate.loanAmount)}
          </p>
          <p className="mt-2 text-xs" style={{ color: "var(--brand-text-muted)" }}>
            Estimated monthly principal &amp; interest
          </p>
          <p
            aria-live="polite"
            className="mt-0.5 text-2xl font-semibold"
            style={{ color: "var(--brand-primary)" }}
          >
            {USD.format(parsed.estimate.monthlyPrincipalAndInterest)}
          </p>
        </div>
      ) : null}

      <p className="mt-3 text-[11px] leading-4" style={{ color: "var(--brand-text-muted)" }}>
        Estimate excludes taxes, insurance, HOA dues, PMI, lender fees, and financial advice.
      </p>
    </section>
  );
}
